import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Users, Loader2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { fmtServerDateTime } from './utils/datetime';
import { StaffTableRow } from './team/StaffTableRow';
import { AddStaffDialog } from './team/AddStaffDialog';
import { ResetPasswordDialog } from './team/ResetPasswordDialog';

const fmtTime = (iso) => {
  if (!iso) return '—';
  return fmtServerDateTime(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function AdminTeamPage() {
  const { admin, loading } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  // Add-staff dialog state (owned here so opening / closing resets cleanly)
  const [addOpen, setAddOpen] = useState(false);
  const [stage, setStage] = useState('details'); // 'details' | 'verify'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('license_manager');
  const [initialPw, setInitialPw] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [waOtp, setWaOtp] = useState('');
  const [otpInfo, setOtpInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset-password dialog state (target truthy → open)
  const [resetTarget, setResetTarget] = useState(null);
  const [resetNewPw, setResetNewPw] = useState('');
  const [resetEmailOtp, setResetEmailOtp] = useState('');
  const [resetWaOtp, setResetWaOtp] = useState('');
  const [resetOtpInfo, setResetOtpInfo] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const loadStaff = async () => {
    setFetching(true);
    try {
      const r = await adminApi.get('/admin/admin-team');
      setItems(r.data?.items || []);
    } catch {
      toast.error('Could not load staff list');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { if (admin) loadStaff(); }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const resetAddForm = () => {
    setStage('details');
    setEmail(''); setName(''); setRole('license_manager');
    setInitialPw(''); setEmailOtp(''); setWaOtp(''); setOtpInfo(null);
  };

  const suggestPassword = async () => {
    try {
      const r = await adminApi.get('/admin/admin-team/suggest-password');
      setInitialPw(r.data.password);
    } catch {
      toast.error('Could not generate password');
    }
  };

  const initiateAdd = async () => {
    if (!email || !name) {
      toast.error('Email, name, and role are required');
      return;
    }
    if (initialPw.length < 8) {
      toast.error('Initial password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminApi.post('/admin/admin-team/initiate-create', { email, name, role });
      setOtpInfo(r.data);
      setStage('verify');
      toast.success('OTPs dispatched');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start staff creation');
    } finally {
      setSubmitting(false);
    }
  };

  const verifyAdd = async () => {
    if (emailOtp.length !== 6 || waOtp.length !== 6) {
      toast.error('Both OTPs must be 6 digits');
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.post('/admin/admin-team', {
        email, name, role,
        initial_password: initialPw,
        email_otp: emailOtp,
        whatsapp_otp: waOtp,
      });
      toast.success('Staff member created');
      setAddOpen(false);
      resetAddForm();
      loadStaff();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not create staff');
    } finally {
      setSubmitting(false);
    }
  };

  const initiateReset = async (staff) => {
    setResetTarget(staff);
    setResetNewPw(''); setResetEmailOtp(''); setResetWaOtp(''); setResetOtpInfo(null);
    try {
      const r = await adminApi.post(`/admin/admin-team/${staff.id}/initiate-reset`);
      setResetOtpInfo(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start reset');
      setResetTarget(null);
    }
  };

  const submitReset = async () => {
    if (resetNewPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (resetEmailOtp.length !== 6 || resetWaOtp.length !== 6) {
      toast.error('Both OTPs must be 6 digits'); return;
    }
    setResetSubmitting(true);
    try {
      await adminApi.post(`/admin/admin-team/${resetTarget.id}/reset`, {
        new_password: resetNewPw,
        email_otp: resetEmailOtp,
        whatsapp_otp: resetWaOtp,
      });
      toast.success('Staff password reset');
      setResetTarget(null);
      loadStaff();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not reset password');
    } finally {
      setResetSubmitting(false);
    }
  };

  const revoke = async (staff) => {
    if (!window.confirm(`Revoke access for ${staff.email}? This cannot be undone here.`)) return;
    try {
      await adminApi.delete(`/admin/admin-team/${staff.id}`);
      toast.success('Staff revoked');
      loadStaff();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not revoke');
    }
  };

  return (
    <div
      data-testid="admin-team-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" data-testid="team-back" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Users className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Admin Team</div>
            <div className="text-xs text-slate-400">Manage role-restricted staff who help operate the SuperAdmin panel.</div>
          </div>
          <Button onClick={loadStaff} disabled={fetching} variant="outline" size="sm" data-testid="team-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => { resetAddForm(); setAddOpen(true); }} size="sm" data-testid="team-add-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Add staff
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
          {fetching ? (
            <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              No admin staff yet. Click <span className="text-amber-300">Add staff</span> to invite one.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="team-table">
              <thead className="bg-slate-950/60">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Last login</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {items.map((s) => (
                  <StaffTableRow
                    key={s.id}
                    staff={s}
                    fmtTime={fmtTime}
                    onReset={initiateReset}
                    onRevoke={revoke}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <AddStaffDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        stage={stage}
        email={email} setEmail={setEmail}
        name={name} setName={setName}
        role={role} setRole={setRole}
        initialPw={initialPw} setInitialPw={setInitialPw}
        emailOtp={emailOtp} setEmailOtp={setEmailOtp}
        waOtp={waOtp} setWaOtp={setWaOtp}
        otpInfo={otpInfo}
        submitting={submitting}
        onSuggestPassword={suggestPassword}
        onInitiate={initiateAdd}
        onVerify={verifyAdd}
      />

      <ResetPasswordDialog
        target={resetTarget}
        onClose={() => setResetTarget(null)}
        newPw={resetNewPw} setNewPw={setResetNewPw}
        emailOtp={resetEmailOtp} setEmailOtp={setResetEmailOtp}
        waOtp={resetWaOtp} setWaOtp={setResetWaOtp}
        otpInfo={resetOtpInfo}
        submitting={resetSubmitting}
        onSubmit={submitReset}
      />
    </div>
  );
}

export default AdminTeamPage;
